import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, PauseCircle, PlayCircle, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  active: { l: "Active", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
  trial: { l: "Essai", cls: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300" },
  suspended: { l: "Suspendue", cls: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300" },
  expired: { l: "Expirée", cls: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
};

export default function SuperAdminCompanies() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => api.get("/superadmin/companies").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const filtered = items.filter((c) => {
    if (statusFilter !== "all" && (c.plan_status || "active") !== statusFilter) return false;
    if (q && ![c.name, c.email, c.phone].filter(Boolean).some((v) => v.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const setStatus = async (c, newStatus) => {
    if (!confirm(`Passer "${c.name}" en ${STATUS[newStatus].l} ?`)) return;
    try { await api.patch(`/superadmin/companies/${c.id}`, { plan_status: newStatus }); toast.success("Statut mis à jour"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changePlan = async (c, plan) => {
    try { await api.patch(`/superadmin/companies/${c.id}`, { plan }); toast.success(`Plan → ${plan}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Entreprises</h1>
        <p className="text-sm text-slate-500">{items.length} entreprises enregistrées</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="sa-companies-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-48" data-testid="sa-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="trial">Essai</SelectItem>
            <SelectItem value="suspended">Suspendue</SelectItem>
            <SelectItem value="expired">Expirée</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="text-left p-3">Nom</th>
                <th className="text-left">Contact</th>
                <th className="text-left">Plan</th>
                <th className="text-left">Statut</th>
                <th className="text-right">Utilisateurs</th>
                <th className="text-right">Billets</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {filtered.map((c) => {
                const s = STATUS[c.plan_status || "active"] || STATUS.active;
                const isActive = (c.plan_status || "active") === "active";
                return (
                  <tr key={c.id} data-testid={`sa-company-row-${c.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-900/50">
                    <td className="p-3">
                      <div className="font-semibold">{c.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{c.id.slice(0, 8)}…</div>
                    </td>
                    <td className="text-xs">
                      <div>{c.email}</div>
                      <div className="text-slate-500">{c.phone}</div>
                    </td>
                    <td>
                      <Select value={c.plan || "FREE"} onValueChange={(v) => changePlan(c, v)}>
                        <SelectTrigger className="w-28" data-testid={`sa-plan-${c.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="FREE">FREE</SelectItem>
                          <SelectItem value="BASIC">BASIC</SelectItem>
                          <SelectItem value="PRO">PRO</SelectItem>
                          <SelectItem value="BUSINESS">BUSINESS</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.cls}`}>{s.l}</span></td>
                    <td className="text-right">{c.users_count}</td>
                    <td className="text-right">{c.tickets_count}</td>
                    <td className="p-3 text-right">
                      {isActive ? (
                        <Button variant="ghost" size="sm" onClick={() => setStatus(c, "suspended")} data-testid={`sa-suspend-${c.id}`}><PauseCircle className="w-4 h-4 mr-1" /> Suspendre</Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => setStatus(c, "active")} data-testid={`sa-activate-${c.id}`}><PlayCircle className="w-4 h-4 mr-1" /> Activer</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">Aucune entreprise</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
